// Safety valve for watchJudgeMe()'s MutationObserver: caps how many mutation batches it
// watches before giving up on a product that never gets a matching review. Generous and
// approximate, not a precise timer - unrelated DOM churn (lazy images, cart drawer, etc.)
// ticks it too.
const JUDGE_ME_WATCH_MAX_ATTEMPTS = 200;

if (!customElements.get('product-reviews')) {
  customElements.define(
    'product-reviews',
    class ProductReviews extends HTMLElement {
      swiper = undefined;
      jdgmObserver = undefined;
      photoPool = [];

      connectedCallback() {
        // Snapshot the theme-editor photos before any Judge.me render can replace the
        // slides. Judge.me exposes no reviewer photos (see readCarouselItems), so the
        // design's lifestyle image always comes from these merchant-uploaded blocks.
        this.photoPool = Array.from(this.querySelectorAll('[data-carousel-track] .product-reviews__photo-image')).map(
          (img) => img.currentSrc || img.src,
        );

        this.initSwiper();
        this.bindLeaveReview();
        this.watchJudgeMe();
      }

      disconnectedCallback() {
        this.swiper?.destroy(true, true);
        this.jdgmObserver?.disconnect();
      }

      get carouselEl() {
        return this.querySelector('[data-carousel]');
      }

      get track() {
        return this.querySelector('[data-carousel-track]');
      }

      get productHandle() {
        return this.dataset.productHandle;
      }

      /** Autoplay is skipped for a single card and when reduced motion is requested. */
      initSwiper() {
        if (typeof Swiper === 'undefined' || !this.carouselEl) {
          return;
        }

        const paginationEl = this.querySelector('.product-reviews__carousel-pagination');
        const slideCount = this.querySelectorAll('[data-carousel-track] > .swiper-slide').length;
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const hasMultipleSlides = slideCount > 1;
        const shouldAutoplay = hasMultipleSlides && !prefersReducedMotion;
        const autoplayConfig = { delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true };

        this.swiper = new Swiper(this.carouselEl, {
          slidesPerView: 1,
          autoHeight: true,
          watchOverflow: true,
          loop: shouldAutoplay,
          autoplay: shouldAutoplay ? autoplayConfig : false,
          pagination: paginationEl ? { el: paginationEl, clickable: true } : false,
        });
      }

      bindLeaveReview() {
        for (const button of this.querySelectorAll('[data-leave-review]')) {
          button.addEventListener('click', this.onLeaveReviewClick.bind(this));
        }
      }

      /**
       * Judge.me renders asynchronously and its app block lives in its own section
       * elsewhere on the page, so watch the document until its widget markup appears.
       *
       * Two independent things happen on every tick, not just on a successful match:
       * 1. hideJudgeMeSource() runs unconditionally, as soon as the widget exists at all.
       *    A product with zero matching reviews still has Judge.me's raw widget in the
       *    DOM (it's store-wide - see readCarouselItems) - without this it sat visible
       *    right next to our themed fallback reviews, showing the same section twice in
       *    two different designs.
       * 2. Review harvesting/rendering only happens once, when a match is found.
       *
       * Deferred one frame: Judge.me's data is frequently already on the page by the time
       * this custom element connects, which made the very first tick() succeed
       * synchronously, right in the same call stack as the initSwiper() above it in
       * connectedCallback() - destroying and recreating the Swiper instance before that
       * first init had finished meant it never picked up any slides (`swiper.slides` came
       * back empty, breaking drag/swipe entirely).
       * @returns {void}
       */
      watchJudgeMe() {
        let attempts = 0;
        let rendered = false;

        const tick = () => {
          attempts += 1;
          this.hideJudgeMeSource();

          if (!rendered) {
            const reviews = this.collectJudgeMeReviews();
            if (reviews.length > 0) {
              rendered = true;
              this.renderReviews(reviews);
            }
          }

          if (rendered || attempts >= JUDGE_ME_WATCH_MAX_ATTEMPTS) {
            this.jdgmObserver?.disconnect();
          }
        };

        requestAnimationFrame(() => {
          tick();
          if (rendered) {
            return;
          }

          this.jdgmObserver = new MutationObserver(tick);
          this.jdgmObserver.observe(document.body, { childList: true, subtree: true });
        });
      }

      /**
       * @typedef {Object} Review
       * @property {number} rating
       * @property {string} quote
       * @property {string} author
       * @property {boolean} verified - Only ever true when the source actually says so;
       *   never inferred, since "Verified Customer" is a trust claim.
       */

      /**
       * Judge.me ships several widget types with different markup. Prefer the per-product
       * Review Widget (`.jdgm-rev`) when it's on the page; otherwise fall back to the
       * store-wide Featured Carousel (`.jdgm-carousel-item`) filtered down to this
       * product, since that carousel mixes every product plus shop-level reviews.
       * @returns {Review[]}
       */
      collectJudgeMeReviews() {
        const widgetReviews = this.readWidgetReviews();
        return widgetReviews.length > 0 ? widgetReviews : this.readCarouselItems();
      }

      /**
       * Reads Judge.me's per-product Review Widget. Already scoped to one product, so no
       * filtering needed.
       * @returns {Review[]}
       */
      readWidgetReviews() {
        return Array.from(document.querySelectorAll('.jdgm-rev')).map((review) => ({
          rating: Number(review.dataset.score) || review.querySelectorAll('.jdgm-star.jdgm--on').length || 5,
          quote: review.querySelector('.jdgm-rev__body')?.textContent.trim() ?? '',
          author: review.querySelector('.jdgm-rev__author')?.textContent.trim() ?? '',
          verified: review.querySelector('.jdgm-rev__buyer-badge') !== null,
        }));
      }

      /**
       * Reads Judge.me's Featured Carousel, keeping only items whose product link points
       * at this product. Matching on the handle in the href rather than the visible title
       * because the title is truncated/localised in that markup. Shop-level reviews have
       * no matching product link, so they drop out here.
       *
       * Note: this markup carries no reviewer photo and no verified-buyer badge - only
       * rating, body, and name are available.
       * @returns {Review[]}
       */
      readCarouselItems() {
        if (!this.productHandle) {
          return [];
        }

        const items = Array.from(document.querySelectorAll('.jdgm-carousel-item'));

        return items
          .filter((item) => {
            const link = item.querySelector('.jdgm-carousel-item__product');
            return link?.getAttribute('href')?.includes(`/products/${this.productHandle}`) ?? false;
          })
          .map((item) => ({
            rating: item.querySelectorAll('.jdgm-star.jdgm--on').length || 5,
            quote: item.querySelector('.jdgm-carousel-item__review-body')?.textContent.trim() ?? '',
            author: item.querySelector('.jdgm-carousel-item__reviewer-name')?.textContent.trim() ?? '',
            verified: false,
          }))
          .filter((review) => review.quote.length > 0);
      }

      /**
       * @param {Review[]} reviews
       * @returns {void}
       */
      renderReviews(reviews) {
        if (!this.track) {
          return;
        }

        this.swiper?.destroy(true, true);
        this.track.innerHTML = reviews.map((review, index) => this.slideMarkup(review, index)).join('');
        this.initSwiper();
        this.bindLeaveReview();
      }

      /**
       * Judge.me review text is pulled via `.textContent`, but re-inserting it through
       * `innerHTML` in slideMarkup() would let literal `<`/`>` characters in a review (e.g.
       * a reviewer typing raw HTML) execute as markup. Escape before interpolating.
       * @param {string} value
       * @returns {string}
       */
      escapeHtml(value) {
        const div = document.createElement('div');
        div.textContent = value;
        return div.innerHTML;
      }

      /**
       * @param {Review} review
       * @param {number} index
       * @returns {string}
       */
      slideMarkup(review, index) {
        const starIcon = this.querySelector('[data-review-star-icon]').innerHTML;
        const verifiedIcon = this.querySelector('[data-review-verified-icon]').innerHTML;
        const productCard = this.querySelector('[data-review-product-card]').innerHTML;
        const stars = Array(review.rating).fill(`<span class="product-reviews__star">${starIcon}</span>`).join('');
        const photo = this.escapeHtml(
          this.photoPool.length > 0 ? this.photoPool[index % this.photoPool.length] : this.dataset.placeholderImage,
        );
        const author = this.escapeHtml(review.author);
        const quote = this.escapeHtml(review.quote);
        const verifiedBadge = review.verified
          ? `<span class="product-reviews__verified">
              <span class="product-reviews__verified-icon">${verifiedIcon}</span>
              <span class="product-reviews__verified-text">${this.dataset.verifiedText}</span>
            </span>`
          : '';

        return `
          <div class="product-reviews__carousel-card swiper-slide">
            <div class="product-reviews__card">
              <div class="product-reviews__card-main">
                <div class="product-reviews__card-photo">
                  <img class="product-reviews__photo-image" src="${photo}" alt="" loading="lazy">
                </div>
                <div class="product-reviews__card-content">
                  <div class="product-reviews__quote-stars">${stars}</div>
                  <blockquote class="product-reviews__quote">${quote}</blockquote>
                  <div class="product-reviews__author">
                    <span class="product-reviews__author-name">${author}</span>
                    ${verifiedBadge}
                  </div>
                </div>
              </div>
              ${productCard}
            </div>
          </div>
        `;
      }

      /**
       * The Judge.me widget is only a data source for this section, so hide it whenever it
       * exists in the DOM - regardless of whether this product actually had any matching
       * reviews to harvest from it. A zero-match product (see readCarouselItems) would
       * otherwise show Judge.me's raw widget sitting right next to our themed fallback
       * reviews: the same section rendered twice in two different designs.
       *
       * Hides every matching widget, not just the first: a merchant can add both the
       * per-product Review Widget AND the store-wide Carousel at once, and each is a
       * separate element in the DOM.
       *
       * Visually hidden rather than `display: none` on purpose: Judge.me's Review Widget
       * carries the real "write a review" control that onLeaveReviewClick() clicks, and
       * that stops working inside a display:none subtree.
       * @returns {void}
       */
      hideJudgeMeSource() {
        const sources = document.querySelectorAll('.jdgm-carousel-wrapper, .jdgm-review-widget');
        for (const source of sources) {
          const target = source.closest('.shopify-app-block') ?? source;
          target.classList.add('product-reviews__jdgm-source--hidden');
        }
      }

      /**
       * Hand off to Judge.me's own "write a review" control when it exists, so the button
       * opens the real form instead of just jumping to an anchor. Multiple app blocks can
       * each carry their own trigger - prefer one that isn't sitting inside a widget we've
       * hidden via hideJudgeMeSource(), falling back to any match if that's all there is.
       * @param {MouseEvent} event
       * @returns {void}
       */
      onLeaveReviewClick(event) {
        const triggers = Array.from(document.querySelectorAll('.jdgm-write-rev-link'));
        const writeReviewTrigger = triggers.find((trigger) => !trigger.closest('.product-reviews__jdgm-source--hidden')) ?? triggers[0];
        if (!writeReviewTrigger) {
          return;
        }

        event.preventDefault();
        writeReviewTrigger.click();
      }
    },
  );
}
